import { useEffect, useMemo, useState, type JSX } from 'react';
import { useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Spread } from '../shared/components/Spread.js';
import { RevealCast } from './components/RevealCast.js';
import type { GuessRevealView } from './types.js';
import './reveal-arc.css';
import './GuessReveal.css';

export interface GuessRevealVersoProps {
  view: GuessRevealView;
}

/**
 * The GuessReveal verso content — extracted as a named export so GameEnd
 * can mount it during the ghost-win burn cinematic underlay. The static
 * cast persists on the verso with the guessing Ghost struck. Owns its
 * own struckIds memo (RevealCast is the only consumer, and it lives here).
 */
export function GuessRevealVerso({ view }: GuessRevealVersoProps): JSX.Element {
  const struckIds = useMemo(() => new Set<string>([view.guessingGhost.id]), [view.guessingGhost.id]);
  return (
    <div className="susdle-reveal-verso">
      <RevealCast players={view.cast} struckIds={struckIds} />
    </div>
  );
}

export interface GuessRevealRectoProps {
  view: GuessRevealView;
}

/**
 * The GuessReveal recto content — extracted as a named export so GameEnd
 * can mount it on the closing leaf's recto face during the ghost-win
 * burn cinematic underlay.
 *
 * Visual only (aria-hidden). The accessible announcement of the guess
 * lives in a deferred sr-only live region in the parent GuessReveal
 * component (NOT here) — that timing (a 1.4s defer so SR users hear the
 * guess as it visually lands rather than read it off the DOM on mount)
 * belongs to the live phase render, not the cinematic underlay.
 */
export function GuessRevealRecto({ view }: GuessRevealRectoProps): JSX.Element {
  const { t } = useTranslation();
  const word = view.guess.toUpperCase();
  const said = t('reveal.guessReveal.said', { name: view.guessingGhost.name });
  return (
    <div className="susdle-reveal-recto susdle-guess-reveal" aria-hidden>
      <p className="susdle-guess-reveal__said">{said}</p>
      <div className="susdle-guess-reveal__word">{word}</div>
    </div>
  );
}

interface GuessRevealProps {
  view: GuessRevealView;
}

/**
 * GuessReveal — the dramatic beat of WHAT the Ghost guessed, and nothing more.
 * "{Name} guessed…" holds alone, then the guessed word resolves in (mono, the
 * letter-spacing settling from wide to tight). No secret word, no verdict —
 * those belong to GameEnd. Cooled-night spread, same static cast on the verso.
 */
export function GuessReveal({ view }: GuessRevealProps): JSX.Element {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  const word = view.guess.toUpperCase();
  const said = t('reveal.guessReveal.said', { name: view.guessingGhost.name });

  /* The word resolves visually ~1.4s in (the said-line cards in, then a
     700ms-delayed 700ms word reveal — see GuessReveal.css). Defer the
     screen-reader announcement to match, so SR users hear the guess as it lands
     rather than read it off the DOM on mount. Reduced motion (where the CSS
     collapses to instant) announces at once. */
  const [announced, setAnnounced] = useState(false);
  useEffect(() => {
    if (reduceMotion) {
      setAnnounced(true);
      return undefined;
    }
    const id = window.setTimeout(() => setAnnounced(true), 1400);
    return () => window.clearTimeout(id);
  }, [reduceMotion]);

  /* Palette persists per viewer (see GhostGuessing): Ghost cool-night, village
     warm candlelight. */
  const cooled = view.pov === 'ghost';

  return (
    <div className="susdle-reveal" data-mood={cooled ? 'night' : 'candle'}>
      <h1 className="susdle-sr-only">{t('reveal.guessReveal.ariaLabel')}</h1>
      <p className="susdle-sr-only" role="status" aria-live="polite">
        {announced ? `${said} ${word}` : ''}
      </p>
      <Spread
        pov={view.pov}
        ariaLabel={t('reveal.guessReveal.ariaLabel')}
        overlay={<div className="susdle-spread-candle" data-mood={cooled ? 'night' : 'reveal'} aria-hidden />}
        verso={<GuessRevealVerso view={view} />}
        recto={<GuessRevealRecto view={view} />}
      />
    </div>
  );
}
